import React, { useState } from 'react'
import Button from 'components/button'
import Tooltip from 'components/tooltip'
import { Cross } from 'components/icons'
import { isValidEmail } from 'helpers/utils'

// Between addresses typed or pasted: commas, semicolons, spaces and new lines.
const SEPARATORS = /[\s,;]+/
const ENDS_AN_ADDRESS = [',', ';', ' ', 'Enter']

/** The addresses in some text, each once, leaving out those already chosen. */
export const addressesIn = (text: string, chosen: string[]): string[] =>
  text
    .split(SEPARATORS)
    .map((address) => address.trim())
    .filter(Boolean)
    .reduce<string[]>(
      (found, address) =>
        [...chosen, ...found].some((each) => each.toLowerCase() === address.toLowerCase())
          ? found
          : [...found, address],
      []
    )

interface EmailsFieldProps {
  id: string
  emails: string[]
  onChange: (emails: string[]) => void
  /** What's typed and not yet an address of its own, which sending counts too. */
  draft: string
  onDraftChange: (draft: string) => void
  invalid?: boolean
  describedBy?: string
}

/**
 * Email addresses as chips in one field, as in shadcn/ui's tag inputs: an address becomes a chip on Enter, a
 * comma, a space or leaving the field, and a pasted list becomes a chip each. Backspace in the empty field
 * takes back the last one. An address that isn't one shows in red, to correct or remove.
 */
const EmailsField: React.FC<EmailsFieldProps> = ({
  id,
  emails,
  onChange,
  draft,
  onDraftChange,
  invalid,
  describedBy
}) => {
  const [input, setInput] = useState<HTMLInputElement | null>(null)

  const add = (text: string) => {
    const found = addressesIn(text, emails)
    if (found.length > 0) onChange([...emails, ...found])
    onDraftChange('')
  }
  const remove = (email: string) => {
    onChange(emails.filter((each) => each !== email))
    input?.focus()
  }

  return (
    // A press anywhere in the field puts the cursor in it, as in a text box.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      className={`flex min-h-9 w-full cursor-text flex-wrap items-center gap-1 rounded-lg border bg-field px-1.5 py-1 transition-[border-color,box-shadow] duration-150 focus-within:ring-3 ${
        invalid
          ? 'border-red focus-within:ring-red/15'
          : 'border-divider hover:border-faint focus-within:border-blue focus-within:ring-blue/15'
      }`}
      onClick={(event) => {
        if (event.target === event.currentTarget) input?.focus()
      }}
    >
      {emails.map((email) => {
        const valid = isValidEmail(email)
        const chip = (
          <span
            key={email}
            className={`inline-flex h-6 max-w-full items-center gap-0.5 rounded-md pr-0.5 pl-2 text-[13px] leading-5 ${
              valid ? 'bg-surface-hover text-body' : 'bg-red/10 text-red'
            }`}
          >
            <span className="truncate">{email}</span>
            <Button
              internalType="transparent"
              aria-label={`Remove ${email}`}
              className="size-5 shrink-0 rounded p-0 text-current hover:bg-black/5 [&_svg]:size-3.5"
              onClick={() => remove(email)}
            >
              <Cross />
            </Button>
          </span>
        )
        return valid ? (
          chip
        ) : (
          <Tooltip key={email} content="Not a valid email address">
            {chip}
          </Tooltip>
        )
      })}
      <input
        ref={setInput}
        id={id}
        type="email"
        multiple
        autoComplete="off"
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        placeholder={emails.length === 0 ? 'name@company.com' : undefined}
        value={draft}
        // Bare, inside the field's own border: style.scss's rules for inputs stay off it.
        data-slot="input"
        className="h-6 min-w-[160px] flex-1 border-0 bg-transparent px-1.5 text-[13px] leading-5 text-body outline-none placeholder:text-faint"
        onChange={(event) => {
          const text = event.target.value
          // A separator typed ends the address before it.
          if (SEPARATORS.test(text)) add(text)
          else onDraftChange(text)
        }}
        onKeyDown={(event) => {
          if (ENDS_AN_ADDRESS.includes(event.key) && draft.trim()) {
            event.preventDefault()
            add(draft)
          } else if (event.key === 'Backspace' && draft === '' && emails.length > 0) {
            onChange(emails.slice(0, -1))
          }
        }}
        onPaste={(event) => {
          event.preventDefault()
          add(`${draft} ${event.clipboardData.getData('text')}`)
        }}
        onBlur={() => add(draft)}
      />
    </div>
  )
}

export default EmailsField
